import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Cloud as CloudIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const features = [
  {
    icon: <DashboardIcon sx={{ fontSize: 40 }} />,
    title: "Modern Dashboard",
    description:
      "Beautiful and intuitive dashboard with real-time analytics and data visualization.",
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: "Secure & Reliable",
    description:
      "Enterprise-grade security with role-based access control and data protection.",
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: "Fast Performance",
    description:
      "Optimized for speed with modern React architecture and efficient data handling.",
  },
  {
    icon: <CloudIcon sx={{ fontSize: 40 }} />,
    title: "Cloud Ready",
    description:
      "Built for the cloud with scalable architecture and seamless API integration.",
  },
];

export const Landing = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  useEffect(() => {
    // Redirect to login page temporarily for demo purposes
    navigate("/login");
  }, []);

  return (
    <Box>
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "white",
          color: theme.palette.text.primary,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            🍇 BERRY
          </Typography>
          <Button
            color="inherit"
            onClick={() => navigate("/login")}
            sx={{ mr: 1 }}
          >
            Login
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate("/signup")}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            Get Started
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
          pt: 12,
          pb: 8,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                }}
              >
                Modern Admin Dashboard
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                Powerful, flexible, and beautiful admin dashboard built with
                Material-UI and React. Manage your application with ease and
                style.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/signup")}
                  sx={{
                    backgroundColor: "white",
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: alpha("#ffffff", 0.9),
                    },
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/login")}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: alpha("#ffffff", 0.1),
                    },
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Login
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <img
                  src="https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Dashboard Preview"
                  style={{
                    width: "100%",
                    maxWidth: 500,
                    borderRadius: 16,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            textAlign: "center",
            mb: 2,
            fontWeight: 600,
          }}
        >
          Why Choose Berry?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            mb: 6,
            color: theme.palette.text.secondary,
            maxWidth: 600,
            mx: "auto",
          }}
        >
          Our admin dashboard provides everything you need to manage your
          application efficiently
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: "100%",
                  textAlign: "center",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                mb: 2,
                fontWeight: 600,
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                color: theme.palette.text.secondary,
              }}
            >
              Join thousands of users who trust Berry for their admin dashboard
              needs
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/signup")}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                px: 4,
                py: 1.5,
              }}
            >
              Start Your Free Trial
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: theme.palette.grey[100],
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: theme.palette.text.secondary,
            }}
          >
            © 2025 Stock Master. Simplifica tu administración, maximiza tus ganancias. Todos los derechos reservados
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
